import { useUncontrolled } from '@byecode/ui/hooks/useUncontrolled'
import type { Selectors, StyleComponentProps } from '@byecode/ui/theme/types'
import { animated, useSpring } from '@react-spring/web'
import { useGesture } from '@use-gesture/react'
import React, { forwardRef, useEffect, useMemo, useRef } from 'react'

import { Box } from '../../Box'
import { useStyles } from './Column.style'

const minmax = (v: number, min: number, max: number) => {
    return Math.max(Math.min(v, max), min)
}
const snap = (v: number, snapSize: number) => {
    return Math.round(v / snapSize) * snapSize
}
const ITEM_HEIGHT = 44
const INERTIA_FACTOR = ITEM_HEIGHT * 12
const SPRING_CONFIG = {
    tension: 300,
    friction: 32,
    mass: 0.9
}
const INERTIA_CONFIG = {
    tension: 120,
    friction: 24,
    mass: 1.1
}
const BOUNDARY_BOUNCE_CONFIG = {
    tension: 220,
    friction: 20,
    mass: 0.95
}

export type PickerColumnStylesName = Selectors<typeof useStyles>

export interface ColumnProps extends StyleComponentProps<PickerColumnStylesName> {
    data: React.Key[]
    index?: number
    defaultIndex?: number
    onIndexChange?: (index: number) => void
}

export const Column = forwardRef<HTMLDivElement, ColumnProps>((props, ref) => {
    const { data, index, defaultIndex, onIndexChange, classNames, styles, unstyled, ...rest } = props

    const { classes } = useStyles({}, { name: 'm-DatePicker', classNames, styles, unstyled })

    const [springStyle, api] = useSpring(() => ({ y: 0 }))

    const [_index, _setIndex] = useUncontrolled({
        value: index,
        defaultValue: defaultIndex ?? 0,
        onChange: onIndexChange
    })

    const gestureLockRef = useRef(false)
    const lastNotifiedIndexRef = useRef(_index)

    const notifyIndex = (idx: number) => {
        if (lastNotifiedIndexRef.current !== idx) {
            lastNotifiedIndexRef.current = idx
            onIndexChange?.(idx)
        }
    }

    const unlockGesture = (result: { finished?: boolean }) => {
        if (result.finished) {
            gestureLockRef.current = false
        }
    }

    useEffect(() => {
        if (gestureLockRef.current) {
            return
        }
        if (springStyle.y.get() !== _index * -ITEM_HEIGHT) {
            api.start({ y: _index * -ITEM_HEIGHT, immediate: true })
        }
    }, [_index, api, springStyle.y])

    const scrollRef = useRef<HTMLDivElement | null>(null)
    const maxOffset = 0
    const minOffset = useMemo(() => -(data.length - 1) * ITEM_HEIGHT, [data.length])

    const offsetToIndex = (y: number) => {
        const snapped = snap(minmax(y, minOffset, maxOffset), ITEM_HEIGHT)
        return Math.abs(snapped / ITEM_HEIGHT)
    }

    const commitSnap = (y: number, velocity = 0) => {
        const snapped = snap(minmax(y, minOffset, maxOffset), ITEM_HEIGHT)
        const idx = Math.abs(snapped / ITEM_HEIGHT)
        lastNotifiedIndexRef.current = idx
        _setIndex(idx)
        api.start({
            y: snapped,
            immediate: false,
            config: {
                ...SPRING_CONFIG,
                velocity
            },
            onRest: unlockGesture
        })
    }

    const releaseWithInertia = (offsetY: number, velocity: number) => {
        if (offsetY > maxOffset) {
            notifyIndex(offsetToIndex(maxOffset))
            const idx = offsetToIndex(maxOffset)
            lastNotifiedIndexRef.current = idx
            _setIndex(idx)
            api.start({
                y: maxOffset,
                immediate: false,
                config: {
                    ...BOUNDARY_BOUNCE_CONFIG,
                    velocity
                },
                onRest: unlockGesture
            })
            return
        }
        if (offsetY < minOffset) {
            notifyIndex(offsetToIndex(minOffset))
            const idx = offsetToIndex(minOffset)
            lastNotifiedIndexRef.current = idx
            _setIndex(idx)
            api.start({
                y: minOffset,
                immediate: false,
                config: {
                    ...BOUNDARY_BOUNCE_CONFIG,
                    velocity
                },
                onRest: unlockGesture
            })
            return
        }

        const clampedOffset = minmax(offsetY, minOffset, maxOffset)
        const projected = minmax(clampedOffset + velocity * INERTIA_FACTOR, minOffset, maxOffset)
        notifyIndex(offsetToIndex(projected))

        if (Math.abs(springStyle.y.get() - projected) < 1) {
            commitSnap(projected)
            return
        }

        api.start({
            y: projected,
            immediate: false,
            config: {
                ...INERTIA_CONFIG,
                velocity
            },
            onRest: result => {
                if (result.finished) {
                    commitSnap(projected)
                }
            }
        })
    }

    useGesture(
        {
            onDrag: state => {
                if (state.tap) {
                    return
                }
                gestureLockRef.current = true
                if (state.last) {
                    const velocity = state.velocity[1] * state.direction[1]
                    releaseWithInertia(state.offset[1], velocity)
                    return
                }
                api.start({ y: state.offset[1], immediate: true })
                notifyIndex(offsetToIndex(state.offset[1]))
            },
            onWheel: state => {
                gestureLockRef.current = true
                if (state.last) {
                    const velocity = state.velocity[1] * state.direction[1]
                    releaseWithInertia(state.offset[1], velocity)
                    return
                }
                const clampedY = minmax(state.offset[1], minOffset, maxOffset)
                api.start({ y: clampedY, immediate: true })
                notifyIndex(offsetToIndex(clampedY))
            }
        },
        {
            target: scrollRef,
            eventOptions: { passive: false },
            drag: {
                axis: 'y',
                from: () => [0, springStyle.y.get()],
                filterTaps: true,
                pointer: {
                    touch: true,
                    mouse: true
                },
                preventDefault: true,
                bounds: { top: minOffset, bottom: maxOffset },
                rubberband: 0.55
            },
            wheel: {
                axis: 'y',
                from: () => [0, springStyle.y.get()],
                preventDefault: true
            }
        }
    )

    return (
        <Box className={classes.column} ref={ref} {...rest}>
            <animated.div
                style={{ ...springStyle, paddingTop: ITEM_HEIGHT * 2, paddingBottom: ITEM_HEIGHT * 2, willChange: 'transform' }}
                ref={scrollRef}
            >
                {data.map(item => (
                    <Box key={item} className={classes.columnItem} style={{ height: ITEM_HEIGHT }}>
                        {item}
                    </Box>
                ))}
            </animated.div>
        </Box>
    )
})
